import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { KPICard } from "@/components/collections/kpi-card";
import {
  Search,
  DollarSign,
  Receipt as ReceiptIcon,
  Target,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  AlertCircle,
  CheckCircle2,
  X,
} from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { useReceiptsPageData } from "@/hooks/useReceiptsData";
import { Receipt, ReceiptKPIs } from "@/types/payments";

type SortField = "receiptNumber" | "customerName" | "receiptDate" | "receivedAmount" | "unappliedAmount" | "allocationStatus";
type SortDirection = "asc" | "desc" | null;

export default function ReceiptsDashboard() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [paymentMethodFilter, setPaymentMethodFilter] = useState("all");
  const [promiseFilter, setPromiseFilter] = useState("all");
  const [activeKpiFilter, setActiveKpiFilter] = useState<string | null>(null);
  
  // Sorting state
  const [sortField, setSortField] = useState<SortField | null>("receiptDate");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");

  // Handle KPI card click for filtering
  const handleKpiCardClick = (status: string) => {
    if (activeKpiFilter === status) {
      // Toggle off - clear the filter
      setActiveKpiFilter(null);
      setStatusFilter("all");
    } else {
      // Set new filter
      setActiveKpiFilter(status);
      setStatusFilter(status);
    }
  };

  // Handle status filter change from dropdown
  const handleStatusFilterChange = (value: string) => {
    setStatusFilter(value);
    // Clear KPI filter if user manually changes status filter
    if (value === "all") {
      setActiveKpiFilter(null);
    } else {
      setActiveKpiFilter(value);
    }
  };

  // Clear all filters
  const clearAllFilters = () => {
    setSearchQuery("");
    setStatusFilter("all");
    setPaymentMethodFilter("all");
    setPromiseFilter("all");
    setActiveKpiFilter(null);
  };

  // Check if any filters are active
  const hasActiveFilters = searchQuery !== "" || statusFilter !== "all" || 
    paymentMethodFilter !== "all" || promiseFilter !== "all";

  // Use mode-aware hook for receipts data (Demo mode uses mock data, Live mode uses API)
  const { receipts, isLoading, error } = useReceiptsPageData();

  // Sorting handler
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      if (sortDirection === "asc") {
        setSortDirection("desc");
      } else if (sortDirection === "desc") {
        setSortField("receiptDate");
        setSortDirection("desc");
      }
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  // Get sort icon
  const getSortIcon = (field: SortField) => {
    if (sortField !== field) {
      return <ArrowUpDown className="ml-2 h-4 w-4" />;
    }
    if (sortDirection === "asc") {
      return <ArrowUp className="ml-2 h-4 w-4" />;
    }
    return <ArrowDown className="ml-2 h-4 w-4" />;
  };

  // Filter and sort receipts
  const filteredAndSortedReceipts = useMemo(() => {
    let result = receipts.filter((receipt) => {
      const matchesSearch =
        receipt.customerName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        receipt.receiptNumber.toLowerCase().includes(searchQuery.toLowerCase()) ||
        receipt.paymentReference.toLowerCase().includes(searchQuery.toLowerCase());
      
      const matchesStatus = statusFilter === "all" || receipt.allocationStatus === statusFilter;
      const matchesPaymentMethod = paymentMethodFilter === "all" || receipt.paymentMethod === paymentMethodFilter;
      const matchesPromise = promiseFilter === "all" || 
        (promiseFilter === "has_promise" && receipt.hasPromise) ||
        (promiseFilter === "no_promise" && !receipt.hasPromise);

      return matchesSearch && matchesStatus && matchesPaymentMethod && matchesPromise;
    });

    if (sortField && sortDirection) {
      result = [...result].sort((a, b) => {
        let aValue: any;
        let bValue: any;

        switch (sortField) {
          case "receiptNumber":
            aValue = a.receiptNumber.toLowerCase();
            bValue = b.receiptNumber.toLowerCase();
            break;
          case "customerName":
            aValue = a.customerName.toLowerCase();
            bValue = b.customerName.toLowerCase();
            break;
          case "receiptDate":
            aValue = new Date(a.receiptDate).getTime();
            bValue = new Date(b.receiptDate).getTime();
            break;
          case "receivedAmount":
            aValue = a.receivedAmount;
            bValue = b.receivedAmount;
            break;
          case "unappliedAmount":
            aValue = a.unappliedAmount;
            bValue = b.unappliedAmount;
            break;
          case "allocationStatus":
            aValue = a.allocationStatus;
            bValue = b.allocationStatus;
            break;
          default:
            return 0;
        }

        if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
        if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
        return 0;
      });
    }

    return result;
  }, [receipts, searchQuery, statusFilter, paymentMethodFilter, promiseFilter, sortField, sortDirection]);

  // Calculate KPIs
  const kpis: ReceiptKPIs = useMemo(() => {
    const unallocated = filteredAndSortedReceipts.filter(r => r.allocationStatus === 'unallocated');
    const partiallyAllocated = filteredAndSortedReceipts.filter(r => r.allocationStatus === 'partially_allocated');
    const fullyAllocated = filteredAndSortedReceipts.filter(r => r.allocationStatus === 'fully_allocated');

    return {
      unallocatedCount: unallocated.length,
      unallocatedAmount: unallocated.reduce((sum, r) => sum + r.unappliedAmount, 0),
      partiallyAllocatedCount: partiallyAllocated.length,
      partiallyAllocatedAmount: partiallyAllocated.reduce((sum, r) => sum + r.unappliedAmount, 0),
      fullyAllocatedCount: fullyAllocated.length,
      fullyAllocatedAmount: fullyAllocated.reduce((sum, r) => sum + r.receivedAmount, 0),
      totalReceipts: filteredAndSortedReceipts.length,
      totalAmount: filteredAndSortedReceipts.reduce((sum, r) => sum + r.receivedAmount, 0),
    };
  }, [filteredAndSortedReceipts]);

  // Get allocation status badge
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'unallocated':
        return <Badge variant="destructive">Unallocated</Badge>;
      case 'partially_allocated':
        return <Badge variant="default" className="bg-yellow-500 hover:bg-yellow-600">Partial</Badge>;
      case 'fully_allocated':
        return <Badge variant="default" className="bg-green-600 hover:bg-green-700">Allocated</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  // Get payment method label
  const getPaymentMethodLabel = (method: string) => {
    const labels: Record<string, string> = {
      bank_transfer: 'Bank Transfer',
      credit_card: 'Credit Card',
      direct_debit: 'Direct Debit',
      cheque: 'Cheque',
      cash: 'Cash',
    };
    return labels[method] || method;
  };

  // Format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
    }).format(amount);
  };

  return (
    <div className="flex-1 space-y-4 p-4 md:p-6 pt-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">Receipts</h2>
          <p className="text-muted-foreground">
            Manage customer receipts and allocations
          </p>
        </div>
        <Button>
          <ReceiptIcon className="mr-2 h-4 w-4" />
          Import Receipts
        </Button>
      </div>

      {/* KPI Cards */}
      <div className="grid gap-4 md:grid-cols-3">
        <div 
          onClick={() => handleKpiCardClick('unallocated')}
          className={cn(
            "cursor-pointer transition-all duration-200 hover:scale-[1.02]",
            activeKpiFilter === 'unallocated' && "ring-2 ring-red-500 ring-offset-2 ring-offset-background"
          )}
        >
          <KPICard
            title="Unallocated"
            value={formatCurrency(kpis.unallocatedAmount)}
            subtitle={`${kpis.unallocatedCount} receipts`}
            icon={AlertCircle}
            colorScheme="red"
          />
        </div>
        <div 
          onClick={() => handleKpiCardClick('partially_allocated')}
          className={cn(
            "cursor-pointer transition-all duration-200 hover:scale-[1.02]",
            activeKpiFilter === 'partially_allocated' && "ring-2 ring-amber-500 ring-offset-2 ring-offset-background"
          )}
        >
          <KPICard
            title="Partially Allocated"
            value={formatCurrency(kpis.partiallyAllocatedAmount)}
            subtitle={`${kpis.partiallyAllocatedCount} receipts`}
            icon={DollarSign}
            colorScheme="amber"
          />
        </div>
        <div 
          onClick={() => handleKpiCardClick('fully_allocated')}
          className={cn(
            "cursor-pointer transition-all duration-200 hover:scale-[1.02]",
            activeKpiFilter === 'fully_allocated' && "ring-2 ring-green-500 ring-offset-2 ring-offset-background"
          )}
        >
          <KPICard
            title="Fully Allocated"
            value={formatCurrency(kpis.fullyAllocatedAmount)}
            subtitle={`${kpis.fullyAllocatedCount} receipts`}
            icon={CheckCircle2}
            colorScheme="green"
          />
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search receipts, customers, or references..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-8"
                />
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Select value={statusFilter} onValueChange={handleStatusFilterChange}>
                <SelectTrigger className={cn(
                  "w-[180px]",
                  activeKpiFilter && "ring-2 ring-primary"
                )}>
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="unallocated">Unallocated</SelectItem>
                  <SelectItem value="partially_allocated">Partial</SelectItem>
                  <SelectItem value="fully_allocated">Allocated</SelectItem>
                </SelectContent>
              </Select>
              <Select value={paymentMethodFilter} onValueChange={setPaymentMethodFilter}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Payment Method" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Methods</SelectItem>
                  <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                  <SelectItem value="credit_card">Credit Card</SelectItem>
                  <SelectItem value="direct_debit">Direct Debit</SelectItem>
                  <SelectItem value="cheque">Cheque</SelectItem>
                  <SelectItem value="cash">Cash</SelectItem>
                </SelectContent>
              </Select>
              <Select value={promiseFilter} onValueChange={setPromiseFilter}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Promise Filter" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Receipts</SelectItem>
                  <SelectItem value="has_promise">Has Promise</SelectItem>
                  <SelectItem value="no_promise">No Promise</SelectItem>
                </SelectContent>
              </Select>
              {hasActiveFilters && (
                <Button 
                  variant="outline" 
                  onClick={clearAllFilters}
                  className="gap-2"
                >
                  Clear Filters
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {activeKpiFilter && (
            <div className={cn(
              "mb-4 flex items-center justify-between rounded-lg border p-3",
              activeKpiFilter === 'unallocated' && "border-red-200 bg-red-50 dark:border-red-900 dark:bg-red-950/20",
              activeKpiFilter === 'partially_allocated' && "border-amber-200 bg-amber-50 dark:border-amber-900 dark:bg-amber-950/20",
              activeKpiFilter === 'fully_allocated' && "border-green-200 bg-green-50 dark:border-green-900 dark:bg-green-950/20"
            )}>
              <div className="flex items-center gap-2">
                {activeKpiFilter === 'unallocated' && <AlertCircle className="h-4 w-4 text-red-600 dark:text-red-400" />}
                {activeKpiFilter === 'partially_allocated' && <DollarSign className="h-4 w-4 text-amber-600 dark:text-amber-400" />}
                {activeKpiFilter === 'fully_allocated' && <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />}
                <span className={cn(
                  "text-sm font-medium",
                  activeKpiFilter === 'unallocated' && "text-red-700 dark:text-red-300",
                  activeKpiFilter === 'partially_allocated' && "text-amber-700 dark:text-amber-300",
                  activeKpiFilter === 'fully_allocated' && "text-green-700 dark:text-green-300"
                )}>
                  Filtered by: {activeKpiFilter === 'unallocated' ? 'Unallocated' : 
                               activeKpiFilter === 'partially_allocated' ? 'Partially Allocated' : 
                               'Fully Allocated'} ({filteredAndSortedReceipts.length} {filteredAndSortedReceipts.length === 1 ? 'receipt' : 'receipts'})
                </span>
              </div>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => handleKpiCardClick(activeKpiFilter)}
                className="h-7 gap-1 text-xs"
              >
                <X className="h-3 w-3" />
                Clear
              </Button>
            </div>
          )}
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>
                    <Button
                      variant="ghost"
                      onClick={() => handleSort("receiptNumber")}
                      className="h-8 px-2"
                    >
                      Receipt #
                      {getSortIcon("receiptNumber")}
                    </Button>
                  </TableHead>
                  <TableHead>
                    <Button
                      variant="ghost"
                      onClick={() => handleSort("customerName")}
                      className="h-8 px-2"
                    >
                      Customer
                      {getSortIcon("customerName")}
                    </Button>
                  </TableHead>
                  <TableHead>
                    <Button
                      variant="ghost"
                      onClick={() => handleSort("receiptDate")}
                      className="h-8 px-2"
                    >
                      Date
                      {getSortIcon("receiptDate")}
                    </Button>
                  </TableHead>
                  <TableHead className="text-right">
                    <Button
                      variant="ghost"
                      onClick={() => handleSort("receivedAmount")}
                      className="h-8 px-2"
                    >
                      Received
                      {getSortIcon("receivedAmount")}
                    </Button>
                  </TableHead>
                  <TableHead className="text-right">
                    <Button
                      variant="ghost"
                      onClick={() => handleSort("unappliedAmount")}
                      className="h-8 px-2"
                    >
                      Unapplied
                      {getSortIcon("unappliedAmount")}
                    </Button>
                  </TableHead>
                  <TableHead>
                    <Button
                      variant="ghost"
                      onClick={() => handleSort("allocationStatus")}
                      className="h-8 px-2"
                    >
                      Status
                      {getSortIcon("allocationStatus")}
                    </Button>
                  </TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredAndSortedReceipts.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                      No receipts found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredAndSortedReceipts.map((receipt) => (
                    <TableRow key={receipt.id}>
                      <TableCell className="font-medium">
                        {receipt.receiptNumber}
                      </TableCell>
                      <TableCell>
                        <div>
                          <div className="font-medium">{receipt.customerName}</div>
                          <div className="text-sm text-muted-foreground">
                            {getPaymentMethodLabel(receipt.paymentMethod)}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        {format(new Date(receipt.receiptDate), 'dd MMM yyyy')}
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {formatCurrency(receipt.receivedAmount)}
                      </TableCell>
                      <TableCell className="text-right">
                        <span className={cn(
                          "font-medium",
                          receipt.unappliedAmount > 0 ? "text-orange-600" : "text-muted-foreground"
                        )}>
                          {formatCurrency(receipt.unappliedAmount)}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {getStatusBadge(receipt.allocationStatus)}
                          {receipt.hasPromise && (
                            <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                              <Target className="mr-1 h-3 w-3" />
                              Promise
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        {receipt.allocationStatus === 'fully_allocated' ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(`/payments/allocate/${receipt.id}`)}
                          >
                            View
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            onClick={() => navigate(`/payments/allocate/${receipt.id}`)}
                          >
                            Allocate
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
